import { action, makeObservable, observable } from "mobx";
import CatalogOptionsAPI from "../api/CatalogOptionsAPI";
import {
  CreateOptionBody,
  CreateOptionValuesBody,
  OptionApi,
  OptionDetailApi,
  OptionListQuery,
  OptionValueApi,
  OptionValueListQuery,
  UpdateOptionBody,
  UpdateOptionValueBody,
} from "../../../models/CatalogOptions/types";

export class CatalogOptionsStore {
  catalogOptionsAPI: CatalogOptionsAPI;
  options: OptionApi[] = [];
  optionDetail: OptionDetailApi | null = null;
  limit = 20;
  total_pages = 0;
  total_count = 0;
  error: string | null = null;

  constructor() {
    this.catalogOptionsAPI = new CatalogOptionsAPI();
    makeObservable(this, {
      options: observable,
      optionDetail: observable,
      limit: observable,
      total_pages: observable,
      total_count: observable,
      error: observable,
      clearOptionDetail: action,
      patchListItem: action,
      listOptions: action,
      getOptionById: action,
      listOptionValues: action,
      createOption: action,
      updateOption: action,
      deleteOption: action,
      createValues: action,
      updateValue: action,
      deleteValue: action,
    });
  }

  clearOptionDetail = () => {
    this.optionDetail = null;
  };

  patchListItem = (id: number, patch: Partial<OptionApi>) => {
    const idx = this.options.findIndex((o) => o.id === id);
    if (idx >= 0) {
      this.options[idx] = { ...this.options[idx], ...patch };
    }
  };

  listOptions = async (params: OptionListQuery) => {
    try {
      const response = await this.catalogOptionsAPI.listOptions(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.options = response.list;
      this.error = null;
      return response;
    } catch (error: any) {
      this.error =
        error?.response?.data?.message ||
        "Something went wrong loading catalog options.";
      throw error;
    }
  };

  getOptionById = async (id: number) => {
    try {
      const detail = await this.catalogOptionsAPI.getOptionById(id);
      this.optionDetail = detail;
      return detail;
    } catch (error: any) {
      throw error;
    }
  };

  listOptionValues = async (
    optionId: number,
    params: OptionValueListQuery = {},
  ) => {
    try {
      return await this.catalogOptionsAPI.listOptionValues(optionId, params);
    } catch (error: any) {
      throw error;
    }
  };

  createOption = async (body: CreateOptionBody) => {
    try {
      const created = await this.catalogOptionsAPI.createOption(body);
      this.optionDetail = created;
      return created;
    } catch (error: any) {
      throw error;
    }
  };

  updateOption = async (id: number, body: UpdateOptionBody) => {
    try {
      const updated = await this.catalogOptionsAPI.updateOption(id, body);
      this.optionDetail = updated;
      this.patchListItem(id, updated);
      return updated;
    } catch (error: any) {
      throw error;
    }
  };

  deleteOption = async (id: number) => {
    try {
      const deleted = await this.catalogOptionsAPI.deleteOption(id);
      this.patchListItem(id, deleted);
      if (this.optionDetail?.id === id) {
        this.optionDetail = deleted;
      }
      return deleted;
    } catch (error: any) {
      throw error;
    }
  };

  createValues = async (optionId: number, body: CreateOptionValuesBody) => {
    try {
      const values = await this.catalogOptionsAPI.createValues(optionId, body);
      if (this.optionDetail?.id === optionId) {
        this.optionDetail = {
          ...this.optionDetail,
          values: [...(this.optionDetail.values ?? []), ...values],
        };
      }
      return values;
    } catch (error: any) {
      throw error;
    }
  };

  updateValue = async (
    optionId: number,
    valueId: number,
    body: UpdateOptionValueBody,
  ) => {
    try {
      const updated = await this.catalogOptionsAPI.updateValue(
        optionId,
        valueId,
        body,
      );
      if (this.optionDetail?.id === optionId) {
        this.optionDetail = {
          ...this.optionDetail,
          values: (this.optionDetail.values ?? []).map((v) =>
            v.id === valueId ? updated : v,
          ),
        };
      }
      return updated;
    } catch (error: any) {
      throw error;
    }
  };

  deleteValue = async (optionId: number, valueId: number) => {
    try {
      const deleted = await this.catalogOptionsAPI.deleteValue(
        optionId,
        valueId,
      );
      if (this.optionDetail?.id === optionId) {
        this.optionDetail = {
          ...this.optionDetail,
          values: (this.optionDetail.values ?? []).map((v) =>
            v.id === valueId ? deleted : v,
          ),
        };
      }
      return deleted;
    } catch (error: any) {
      throw error;
    }
  };
}
